import {
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult,
} from "react-aria-components";
import { useController, type Control, type Path } from "react-hook-form";
import { Description, FieldError, FieldProps, Input, Label } from "./Field";
import { composeTailwindRenderProps, focusRing } from "./utils";

export interface TextFieldProps<T extends object>
  extends Omit<AriaTextFieldProps, "name">,
    Pick<FieldProps, "theme"> {
  label?: string;
  description?: string;
  placeholder?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  control?: Control<T>;
  name: Path<T>;
  inputStyles?: string;
  size?: "sm" | "md";
}

export function TextField<T extends object>({
  label,
  description,
  errorMessage,
  placeholder,
  control,
  name,
  size = "md",
  theme,
  inputStyles,
  ...props
}: TextFieldProps<T>) {
  const {
    field,
    fieldState: { error, invalid },
  } = useController({
    control,
    name,
  });

  return (
    <AriaTextField
      {...props}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      className={composeTailwindRenderProps(
        props.className,
        "flex flex-col gap-1",
      )}
      validationBehavior="aria"
      isInvalid={invalid}
    >
      {label && <Label>{label}</Label>}
      <Input
        size={size}
        ref={field.ref}
        placeholder={placeholder}
        className={composeTailwindRenderProps(focusRing, inputStyles || "")}
        theme={theme}
        showErrorIcon={theme === "stacked" && !!error?.message}
        errorIconTooltip={error?.message}
      />
      {description && <Description>{description}</Description>}
      {theme !== "stacked" && (
        <FieldError className="mt-2 ml-[5px]">
          {error?.message || errorMessage}
        </FieldError>
      )}
    </AriaTextField>
  );
}
